import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Briefcase, LogOut, User, FileText, Send, CheckCircle, Clock, 
  AlertCircle, Building, DollarSign, Calendar, Upload, Link as LinkIcon,
  X, Loader2, ChevronRight, MessageSquare, Check, XCircle, Menu
} from 'lucide-react';
import { useViewMode } from '../../contexts/ViewModeContext';
import { ViewModeToggleSimple } from '../../components/ViewModeToggle';

const API_URL = '';

export default function CareerDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isMobileView } = useViewMode();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('roles');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [roles, setRoles] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Application modal
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [applyForm, setApplyForm] = useState({
    applicant_email: '',
    applicant_phone: '',
    confirm_qualifications: false,
    confirm_skills: false,
    confirm_scope: false,
    current_ctc: '',
    expected_ctc: '',
    past_experience: '',
    years_of_experience: '',
    portfolio_urls: [''],
    portfolio_files: [],
    notice_period: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Per-field validation errors for the apply modal (Track A)
  const [formErrors, setFormErrors] = useState({});

  // Track B refs: scroll container + "is user near the bottom" sticky flag.
  // When new messages arrive AND the user was already at the bottom, we
  // auto-scroll. Otherwise we leave their scroll position alone so we don't
  // yank them mid-read.
  const messagesScrollRef = useRef(null);
  const messagesAtBottomRef = useRef(true);
  
  // Application detail modal
  const [showAppDetail, setShowAppDetail] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  const careerApi = axios.create({
    baseURL: API_URL,
    timeout: 30000,
    headers: { Authorization: `Bearer ${localStorage.getItem('career_token')}` }
  });

  const fetchData = useCallback(async () => {
    try {
      const [rolesRes, appsRes, meRes] = await Promise.all([
        careerApi.get('/api/career/roles'),
        careerApi.get('/api/career/applications'),
        careerApi.get('/api/career/auth/me')
      ]);
      setRoles(rolesRes.data || []);
      setApplications(appsRes.data || []);
      setUser(meRes.data);
      setLoading(false);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('career_token');
        localStorage.removeItem('career_user');
        navigate('/career/login');
      }
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem('career_token');
    if (!token) {
      navigate('/career/login');
      return;
    }
    fetchData();
  }, [navigate, fetchData]);

  // Auto-open the apply modal if user landed here from a "Apply Now" click
  // on the public careers page (query param `?role=<id>` or pending in LS).
  // Guard with a ref so we never open the modal twice (StrictMode double-mount
  // + re-render after setSearchParams would otherwise re-fire this effect).
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (autoOpenedRef.current) return;
    if (loading || !roles.length) return;
    let pendingRoleId = searchParams.get('role');
    if (!pendingRoleId) {
      try { pendingRoleId = localStorage.getItem('pending_apply_role'); } catch (e) { /* ignore */ }
    }
    if (!pendingRoleId) return;

    // Mark FIRST so any re-render triggered by state changes below cannot re-enter.
    autoOpenedRef.current = true;

    const role = roles.find((r) => r._id === pendingRoleId);
    if (role) {
      const alreadyApplied = (applications || []).some(
        (a) => (a.role_id === pendingRoleId) || (a.role?._id === pendingRoleId)
      );
      if (alreadyApplied) {
        toast.info('You\'ve already applied to this role — see Applications tab.');
        setActiveTab('applications');
      } else {
        openApplyModal(role);
      }
    } else {
      toast.error('That role is no longer accepting applications.');
    }
    // Clear pending state — single-shot auto-open
    try { localStorage.removeItem('pending_apply_role'); } catch (e) { /* ignore */ }
    if (searchParams.get('role')) {
      const next = new URLSearchParams(searchParams);
      next.delete('role');
      setSearchParams(next, { replace: true });
    }
  }, [loading, roles, applications, searchParams, setSearchParams]);

  // Auto-open the application detail modal when an `?app=<id>` deep link
  // is present (e.g. clicking the "Open chat" button from a notification
  // email). Guarded by a ref so it only runs once.
  const autoOpenedAppRef = useRef(false);
  useEffect(() => {
    if (autoOpenedAppRef.current) return;
    if (loading) return;
    const appId = searchParams.get('app');
    if (!appId) return;
    autoOpenedAppRef.current = true;
    // Find from already-loaded applications first to avoid an extra round-trip
    const match = (applications || []).find((a) => a._id === appId);
    if (match) {
      openAppDetail(match);
    } else {
      // Fall back to direct fetch — handles email links sent before the
      // applicant has refreshed their dashboard.
      careerApi.get(`/api/career/applications/${appId}`)
        .then((r) => { setSelectedApp(r.data); setShowAppDetail(true); })
        .catch(() => { toast.error('Could not load that application.'); });
    }
    // Clean the query param so refresh / back doesn't re-trigger
    const next = new URLSearchParams(searchParams);
    next.delete('app');
    setSearchParams(next, { replace: true });
  }, [loading, applications, searchParams]);

  // Live chat polling (Track B) — refresh the open application's messages
  // every 5s while the detail modal is open. Pauses when the browser tab is
  // hidden so we don't burn requests for inactive tabs.
  useEffect(() => {
    if (!showAppDetail || !selectedApp?._id) return;
    const appId = selectedApp._id;
    let cancelled = false;

    const tick = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const r = await careerApi.get(`/api/career/applications/${appId}`);
        if (cancelled) return;
        const fresh = r.data;
        // Smart diff — only update when message count OR last timestamp OR
        // status / offer fields actually changed. Prevents flicker.
        setSelectedApp((prev) => {
          if (!prev || prev._id !== appId) return prev;
          const prevLen = (prev.messages || []).length;
          const freshLen = (fresh.messages || []).length;
          const prevLast = (prev.messages || []).slice(-1)[0]?.timestamp || '';
          const freshLast = (fresh.messages || []).slice(-1)[0]?.timestamp || '';
          if (
            prevLen !== freshLen ||
            prevLast !== freshLast ||
            prev.status !== fresh.status ||
            prev.offer_status !== fresh.offer_status ||
            prev.offer_amount !== fresh.offer_amount
          ) {
            return fresh;
          }
          return prev;
        });
      } catch (e) { /* silent — best-effort polling */ }
    };

    const interval = setInterval(tick, 5000);
    const onVis = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [showAppDetail, selectedApp?._id]);

  // Auto-scroll the chat to the bottom whenever the messages array length
  // grows AND the user was already at (or near) the bottom. We use the count
  // from selectedApp.messages length as a trigger.
  useEffect(() => {
    if (!showAppDetail) return;
    if (!messagesAtBottomRef.current) return;
    const el = messagesScrollRef.current;
    if (!el) return;
    // Defer one tick so the DOM has the new message painted.
    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
  }, [showAppDetail, selectedApp?.messages?.length]);

  const handleLogout = () => {
    localStorage.removeItem('career_token');
    localStorage.removeItem('career_user');
    navigate('/career/login');
  };

  const openApplyModal = (role) => {
    setSelectedRole(role);
    setFormErrors({});
    setApplyForm({
      applicant_email: user?.email || '',
      applicant_phone: user?.phone || '',
      confirm_qualifications: false,
      confirm_skills: false,
      confirm_scope: false,
      current_ctc: '',
      expected_ctc: '',
      past_experience: '',
      years_of_experience: '',
      portfolio_urls: [''],
      portfolio_files: [],
      notice_period: ''
    });
    setShowApplyModal(true);
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    if (applyForm.portfolio_files.length + files.length > 5) {
      toast.error('Maximum 5 files allowed');
      return;
    }

    setUploading(true);
    try {
      const uploadedUrls = [];
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        const response = await careerApi.post('/api/files/s3-upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (response.data?.url) {
          uploadedUrls.push(response.data.url);
        }
      }
      setApplyForm(prev => ({
        ...prev,
        portfolio_files: [...prev.portfolio_files, ...uploadedUrls]
      }));
      toast.success('Files uploaded');
    } catch (error) {
      toast.error('Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const addUrlField = () => {
    if (applyForm.portfolio_urls.length < 5) {
      setApplyForm(prev => ({
        ...prev,
        portfolio_urls: [...prev.portfolio_urls, '']
      }));
    }
  };

  const updateUrl = (index, value) => {
    const newUrls = [...applyForm.portfolio_urls];
    newUrls[index] = value;
    setApplyForm(prev => ({ ...prev, portfolio_urls: newUrls }));
  };

  const removeUrl = (index) => {
    setApplyForm(prev => ({
      ...prev,
      portfolio_urls: prev.portfolio_urls.filter((_, i) => i !== index)
    }));
  };

  const removeFile = (index) => {
    setApplyForm(prev => ({
      ...prev,
      portfolio_files: prev.portfolio_files.filter((_, i) => i !== index)
    }));
  };

  const validateApplyForm = () => {
    const errs = {};
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneDigits = String(applyForm.applicant_phone || '').replace(/\D/g, '');

    if (!applyForm.applicant_email?.trim()) {
      errs.applicant_email = 'Email is required';
    } else if (!emailRe.test(applyForm.applicant_email.trim())) {
      errs.applicant_email = 'Enter a valid email address';
    }
    if (!applyForm.applicant_phone?.trim()) {
      errs.applicant_phone = 'Mobile number is required';
    } else if (phoneDigits.length < 10) {
      errs.applicant_phone = 'Mobile number must have at least 10 digits';
    }
    if (!applyForm.confirm_qualifications) errs.confirm_qualifications = 'Please confirm you meet the qualifications';
    if (!applyForm.confirm_skills) errs.confirm_skills = 'Please confirm you have the skills';
    if (!applyForm.confirm_scope) errs.confirm_scope = 'Please confirm you understand the scope of work';
    if (!String(applyForm.expected_ctc || '').trim()) {
      errs.expected_ctc = 'Expected CTC is required';
    }
    return errs;
  };

  const focusFirstError = (errs) => {
    const order = [
      'applicant_email', 'applicant_phone',
      'confirm_qualifications', 'confirm_skills', 'confirm_scope',
      'expected_ctc',
    ];
    const firstKey = order.find((k) => errs[k]);
    if (!firstKey) return;
    setTimeout(() => {
      const node = document.querySelector(`[data-error-anchor="${firstKey}"]`);
      if (node) {
        node.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const focusable = node.querySelector('input, textarea, select');
        if (focusable && focusable.focus) focusable.focus({ preventScroll: true });
      }
    }, 50);
  };

  const handleApply = async (e) => {
    e.preventDefault();
    const errs = validateApplyForm();
    if (Object.keys(errs).length > 0) {
      setFormErrors(errs);
      const count = Object.keys(errs).length;
      toast.error(`Please fix ${count} issue${count > 1 ? 's' : ''} above before submitting`);
      focusFirstError(errs);
      return;
    }
    setFormErrors({});

    setSubmitting(true);
    try {
      await careerApi.post('/api/career/applications', {
        role_id: selectedRole._id,
        ...applyForm,
        portfolio_urls: applyForm.portfolio_urls.filter(url => url.trim())
      });
      toast.success('Application submitted successfully!');
      setShowApplyModal(false);
      fetchData();
      setActiveTab('applications');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  const openAppDetail = async (app) => {
    try {
      const response = await careerApi.get(`/api/career/applications/${app._id}`);
      setSelectedApp(response.data);
      setShowAppDetail(true);
    } catch (error) {
      toast.error('Failed to load application details');
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    
    setSendingMessage(true);
    try {
      await careerApi.post(`/api/career/applications/${selectedApp._id}/messages`, {
        message: newMessage
      });
      setNewMessage('');
      // Refresh application
      const response = await careerApi.get(`/api/career/applications/${selectedApp._id}`);
      setSelectedApp(response.data);
      toast.success('Message sent');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const respondToOffer = async (accept) => {
    try {
      await careerApi.post(`/api/career/applications/${selectedApp._id}/offer-response?accept=${accept}`);
      toast.success(`Offer ${accept ? 'accepted' : 'rejected'}`);
      // Refresh
      const response = await careerApi.get(`/api/career/applications/${selectedApp._id}`);
      setSelectedApp(response.data);
      fetchData();
    } catch (error) {
      toast.error('Failed to respond to offer');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Submitted': return 'bg-blue-100 text-blue-700';
      case 'In Review': return 'bg-yellow-100 text-yellow-700';
      case 'Interview': return 'bg-purple-100 text-purple-700';
      case 'Offer Extended': return 'bg-orange-100 text-orange-700';
      case 'Offer Accepted': return 'bg-green-100 text-green-700';
      case 'Rejected': return 'bg-red-100 text-red-700';
      default: return 'bg-neutral-100 text-neutral-700';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Submitted': return <Clock className="w-4 h-4" />;
      case 'In Review': return <AlertCircle className="w-4 h-4" />;
      case 'Interview': return <Calendar className="w-4 h-4" />;
      case 'Offer Extended': return <DollarSign className="w-4 h-4" />;
      case 'Offer Accepted': return <CheckCircle className="w-4 h-4" />;
      default: return <FileText className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white/40">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  // ============================================
  // SHARED MODALS (rendered in BOTH mobile + desktop)
  // Without this, mobile applicants tapped "Apply Now"
  // but the modal JSX only lived inside the desktop return,
  // so the form never appeared — silently breaking the flow.
  // ============================================
  const applyModalJsx = showApplyModal && selectedRole && (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="career-apply-modal">
      <div className="bg-white shadow-xl rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-white/40 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-[#1a1a2e]">Apply for {selectedRole.designation}</h2>
          <button onClick={() => setShowApplyModal(false)} className="p-2 hover:bg-neutral-100 rounded-xl" data-testid="career-apply-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleApply} className="p-6 space-y-6">
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
            <h3 className="font-semibold text-indigo-900 mb-2">Role Summary</h3>
            <p className="text-sm text-indigo-800"><strong>Position:</strong> {selectedRole.designation}</p>
            <p className="text-sm text-indigo-800"><strong>Department:</strong> {selectedRole.department_name}</p>
          </div>

          <div className="space-y-4">
            <h3 className="font-semibold text-[#1a1a2e]">Contact Information <span className="text-red-500">*</span></h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div data-error-anchor="applicant_email">
                <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">Email Address <span className="text-red-500">*</span></label>
                <input type="email" value={applyForm.applicant_email}
                  onChange={(e) => { setApplyForm(prev => ({ ...prev, applicant_email: e.target.value })); if (formErrors.applicant_email) setFormErrors(prev => ({ ...prev, applicant_email: undefined })); }}
                  placeholder="your@email.com"
                  aria-invalid={!!formErrors.applicant_email}
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 ${formErrors.applicant_email ? 'border-red-400 focus:ring-red-300/40' : 'border-white/40 focus:ring-indigo-400/30'}`}
                  data-testid="applicant-email" />
                {formErrors.applicant_email && (
                  <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1" data-testid="error-applicant-email">
                    <AlertCircle className="w-3.5 h-3.5" />{formErrors.applicant_email}
                  </p>
                )}
              </div>
              <div data-error-anchor="applicant_phone">
                <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">Mobile Number <span className="text-red-500">*</span></label>
                <input type="tel" value={applyForm.applicant_phone}
                  onChange={(e) => { setApplyForm(prev => ({ ...prev, applicant_phone: e.target.value })); if (formErrors.applicant_phone) setFormErrors(prev => ({ ...prev, applicant_phone: undefined })); }}
                  placeholder="+91 98765 43210"
                  aria-invalid={!!formErrors.applicant_phone}
                  className={`w-full px-4 py-3 border rounded-xl focus:ring-2 ${formErrors.applicant_phone ? 'border-red-400 focus:ring-red-300/40' : 'border-white/40 focus:ring-indigo-400/30'}`}
                  data-testid="applicant-phone" />
                {formErrors.applicant_phone && (
                  <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1" data-testid="error-applicant-phone">
                    <AlertCircle className="w-3.5 h-3.5" />{formErrors.applicant_phone}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <h3 className="font-semibold text-[#1a1a2e]">Acknowledgements <span className="text-red-500">*</span></h3>
            <div data-error-anchor="confirm_qualifications">
              <label className={`flex items-start gap-3 p-3 border rounded-xl hover:bg-white/60 cursor-pointer ${formErrors.confirm_qualifications ? 'border-red-300 bg-red-50/30' : 'border-white/40'}`}>
                <input type="checkbox" checked={applyForm.confirm_qualifications}
                  onChange={(e) => { setApplyForm(prev => ({ ...prev, confirm_qualifications: e.target.checked })); if (formErrors.confirm_qualifications) setFormErrors(prev => ({ ...prev, confirm_qualifications: undefined })); }}
                  className="mt-1 w-5 h-5 text-indigo-600 rounded"
                  data-testid="ack-qualifications" />
                <span className="text-sm text-neutral-700">
                  I confirm that I have the required <strong>qualifications</strong> mentioned for this role.
                  {selectedRole.qualifications && <span className="block text-neutral-500 mt-1">{selectedRole.qualifications}</span>}
                </span>
              </label>
              {formErrors.confirm_qualifications && (
                <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1" data-testid="error-confirm-qualifications">
                  <AlertCircle className="w-3.5 h-3.5" />{formErrors.confirm_qualifications}
                </p>
              )}
            </div>
            <div data-error-anchor="confirm_skills">
              <label className={`flex items-start gap-3 p-3 border rounded-xl hover:bg-white/60 cursor-pointer ${formErrors.confirm_skills ? 'border-red-300 bg-red-50/30' : 'border-white/40'}`}>
                <input type="checkbox" checked={applyForm.confirm_skills}
                  onChange={(e) => { setApplyForm(prev => ({ ...prev, confirm_skills: e.target.checked })); if (formErrors.confirm_skills) setFormErrors(prev => ({ ...prev, confirm_skills: undefined })); }}
                  className="mt-1 w-5 h-5 text-indigo-600 rounded"
                  data-testid="ack-skills" />
                <span className="text-sm text-neutral-700">
                  I confirm that I have the required <strong>skills</strong> mentioned for this role.
                  {selectedRole.skills && <span className="block text-neutral-500 mt-1">{selectedRole.skills}</span>}
                </span>
              </label>
              {formErrors.confirm_skills && (
                <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1" data-testid="error-confirm-skills">
                  <AlertCircle className="w-3.5 h-3.5" />{formErrors.confirm_skills}
                </p>
              )}
            </div>
            <div data-error-anchor="confirm_scope">
              <label className={`flex items-start gap-3 p-3 border rounded-xl hover:bg-white/60 cursor-pointer ${formErrors.confirm_scope ? 'border-red-300 bg-red-50/30' : 'border-white/40'}`}>
                <input type="checkbox" checked={applyForm.confirm_scope}
                  onChange={(e) => { setApplyForm(prev => ({ ...prev, confirm_scope: e.target.checked })); if (formErrors.confirm_scope) setFormErrors(prev => ({ ...prev, confirm_scope: undefined })); }}
                  className="mt-1 w-5 h-5 text-indigo-600 rounded"
                  data-testid="ack-scope" />
                <span className="text-sm text-neutral-700">
                  I understand the <strong>scope of work</strong> for this role.
                  {selectedRole.scope_of_work && <span className="block text-neutral-500 mt-1">{selectedRole.scope_of_work}</span>}
                </span>
              </label>
              {formErrors.confirm_scope && (
                <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1" data-testid="error-confirm-scope">
                  <AlertCircle className="w-3.5 h-3.5" />{formErrors.confirm_scope}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">Current CTC (if any)</label>
              <input type="text" value={applyForm.current_ctc}
                onChange={(e) => setApplyForm(prev => ({ ...prev, current_ctc: e.target.value }))}
                placeholder="e.g., 5,00,000" className="w-full px-4 py-3 border border-white/40 rounded-xl" />
            </div>
            <div data-error-anchor="expected_ctc">
              <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">Expected Annual CTC <span className="text-red-500">*</span></label>
              <input type="text" value={applyForm.expected_ctc}
                onChange={(e) => { setApplyForm(prev => ({ ...prev, expected_ctc: e.target.value })); if (formErrors.expected_ctc) setFormErrors(prev => ({ ...prev, expected_ctc: undefined })); }}
                placeholder="e.g., 8,00,000"
                aria-invalid={!!formErrors.expected_ctc}
                className={`w-full px-4 py-3 border rounded-xl focus:ring-2 ${formErrors.expected_ctc ? 'border-red-400 focus:ring-red-300/40' : 'border-white/40 focus:ring-indigo-400/30'}`}
                data-testid="expected-ctc" />
              {formErrors.expected_ctc && (
                <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1" data-testid="error-expected-ctc">
                  <AlertCircle className="w-3.5 h-3.5" />{formErrors.expected_ctc}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">Years of Experience</label>
              <input type="text" value={applyForm.years_of_experience}
                onChange={(e) => setApplyForm(prev => ({ ...prev, years_of_experience: e.target.value }))}
                placeholder="e.g., 3 years" className="w-full px-4 py-3 border border-white/40 rounded-xl" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">Notice Period</label>
              <input type="text" value={applyForm.notice_period}
                onChange={(e) => setApplyForm(prev => ({ ...prev, notice_period: e.target.value }))}
                placeholder="e.g., 30 days" className="w-full px-4 py-3 border border-white/40 rounded-xl" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">Past Work Experience</label>
            <textarea value={applyForm.past_experience}
              onChange={(e) => setApplyForm(prev => ({ ...prev, past_experience: e.target.value }))}
              placeholder="Describe your relevant work experience..." rows={3}
              className="w-full px-4 py-3 border border-white/40 rounded-xl" />
          </div>

          <div>
            <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">Portfolio URLs (up to 5)</label>
            {applyForm.portfolio_urls.map((url, index) => (
              <div key={index} className="flex gap-2 mb-2">
                <input type="url" value={url} onChange={(e) => updateUrl(index, e.target.value)}
                  placeholder="https://..." className="flex-1 px-4 py-2 border border-white/40 rounded-xl" />
                {applyForm.portfolio_urls.length > 1 && (
                  <button type="button" onClick={() => removeUrl(index)} className="p-2 text-red-500 hover:bg-red-50 rounded-xl">
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>
            ))}
            {applyForm.portfolio_urls.length < 5 && (
              <button type="button" onClick={addUrlField} className="text-indigo-600 text-sm font-medium hover:text-indigo-700">
                + Add Another URL
              </button>
            )}
          </div>

          <div>
            <label className="block text-sm font-semibold text-[#1a1a2e] mb-2">Portfolio Files (up to 5)</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {applyForm.portfolio_files.map((file, index) => (
                <div key={index} className="flex items-center gap-2 px-3 py-1 bg-neutral-100 rounded-xl text-sm">
                  <FileText className="w-4 h-4" />
                  <span className="truncate max-w-[150px]">{file.split('/').pop()}</span>
                  <button type="button" onClick={() => removeFile(index)} className="text-red-500">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            {applyForm.portfolio_files.length < 5 && (
              <label className="inline-flex items-center gap-2 px-4 py-2 border border-white/40 rounded-xl cursor-pointer hover:bg-white/60">
                <Upload className="w-4 h-4" />
                <span>{uploading ? 'Uploading...' : 'Upload Files'}</span>
                <input type="file" multiple onChange={handleFileUpload} className="hidden" disabled={uploading} />
              </label>
            )}
          </div>

          <div className="flex items-center justify-end gap-4 pt-4 border-t border-white/40">
            <button type="button" onClick={() => setShowApplyModal(false)}
              className="px-6 py-3 border border-white/40 rounded-xl text-neutral-700 hover:bg-white/60 font-medium">
              Cancel
            </button>
            <button type="submit" disabled={submitting}
              className="px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl font-semibold hover:opacity-90 disabled:opacity-50"
              data-testid="career-apply-submit">
              {submitting ? 'Submitting...' : 'Submit Application'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  const appDetailJsx = showAppDetail && selectedApp && (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white shadow-xl rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="sticky top-0 bg-white border-b border-white/40 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-[#1a1a2e]">{selectedApp.designation}</h2>
            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium mt-2 ${getStatusColor(selectedApp.status)}`}>
              {getStatusIcon(selectedApp.status)}
              {selectedApp.status}
            </span>
          </div>
          <button onClick={() => setShowAppDetail(false)} className="p-2 hover:bg-neutral-100 rounded-xl">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <div className="bg-white/40 rounded-xl p-3">
              <p className="text-xs text-neutral-500 uppercase">Department</p>
              <p className="font-medium text-[#1a1a2e]">{selectedApp.department_name}</p>
            </div>
            <div className="bg-white/40 rounded-xl p-3">
              <p className="text-xs text-neutral-500 uppercase">Expected CTC</p>
              <p className="font-medium text-[#1a1a2e]">₹{selectedApp.expected_ctc}</p>
            </div>
            {selectedApp.years_of_experience && (
              <div className="bg-white/40 rounded-xl p-3">
                <p className="text-xs text-neutral-500 uppercase">Experience</p>
                <p className="font-medium text-[#1a1a2e]">{selectedApp.years_of_experience}</p>
              </div>
            )}
            {selectedApp.notice_period && (
              <div className="bg-white/40 rounded-xl p-3">
                <p className="text-xs text-neutral-500 uppercase">Notice Period</p>
                <p className="font-medium text-[#1a1a2e]">{selectedApp.notice_period}</p>
              </div>
            )}
          </div>

          {selectedApp.offer_status === 'Pending' && (
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-6">
              <p className="font-semibold text-orange-800 mb-3">You have a pending offer!</p>
              <p className="text-orange-700 mb-4">Offered Amount: ₹{selectedApp.offer_amount}</p>
              <div className="flex gap-3">
                <button onClick={() => respondToOffer(true)}
                  className="flex-1 px-4 py-2 bg-green-500 text-white rounded-xl font-medium hover:bg-green-600 flex items-center justify-center gap-2">
                  <Check className="w-5 h-5" />
                  Accept Offer
                </button>
                <button onClick={() => respondToOffer(false)}
                  className="flex-1 px-4 py-2 bg-red-500 text-white rounded-xl font-medium hover:bg-red-600 flex items-center justify-center gap-2">
                  <XCircle className="w-5 h-5" />
                  Decline Offer
                </button>
              </div>
            </div>
          )}

          <div className="border border-white/40 rounded-xl overflow-hidden">
            <div className="bg-white/40 px-4 py-3 border-b border-white/40 flex items-center justify-between">
              <h3 className="font-semibold text-[#1a1a2e] flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                Communication
              </h3>
              <span className="inline-flex items-center gap-1.5 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full" data-testid="chat-live-indicator">
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
                Live · refreshing every 5s
              </span>
            </div>
            <div ref={messagesScrollRef} onScroll={(e) => { const el = e.currentTarget; messagesAtBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 30; }} className="h-64 overflow-y-auto p-4 space-y-3 bg-white/40">
              {(!selectedApp.messages || selectedApp.messages.length === 0) ? (
                <p className="text-center text-neutral-500 py-8">No messages yet. Start a conversation!</p>
              ) : (
                selectedApp.messages.map((msg, index) => (
                  <div key={msg.id || `${msg.timestamp}-${index}`}
                    className={`p-3 rounded-xl max-w-[80%] ${
                      msg.sender_type === 'applicant' ? 'bg-indigo-100 ml-auto'
                      : msg.sender_type === 'system' ? 'bg-yellow-50 border border-yellow-200 mx-auto text-center'
                      : 'glass-card'
                    }`}>
                    {msg.sender_type !== 'system' && (
                      <p className="text-xs font-semibold text-neutral-600 mb-1">{msg.sender_name}</p>
                    )}
                    <p className="text-sm text-[#1a1a2e]">{msg.message}</p>
                    <p className="text-xs text-neutral-400 mt-1">{new Date(msg.timestamp).toLocaleString()}</p>
                  </div>
                ))
              )}
            </div>
            <div className="p-3 border-t border-white/40 bg-white">
              <div className="flex gap-2">
                <input type="text" value={newMessage} onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && sendMessage()} placeholder="Type a message..."
                  className="flex-1 px-4 py-2 border border-white/40 rounded-xl" />
                <button onClick={sendMessage} disabled={sendingMessage || !newMessage.trim()}
                  className="px-4 py-2 bg-indigo-500 text-white rounded-xl hover:bg-indigo-600 disabled:opacity-50">
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // ============================================
  // MOBILE VIEW
  // ============================================
  if (isMobileView) {
    return (
      <>
      <div className="min-h-screen bg-white/40 pb-20" data-testid="career-dashboard-mobile">
        {/* Mobile Header */}
        <header className="bg-white border-b border-white/40 sticky top-0 z-40 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                <Briefcase className="w-4 h-4 text-white" />
              </div>
              <span className="text-base font-bold text-[#1a1a2e]">Careers</span>
            </div>
            
            <div className="flex items-center gap-2">
              <ViewModeToggleSimple />
              <button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="p-2 hover:bg-neutral-100 rounded-xl"
              >
                <Menu className="w-5 h-5 text-neutral-600" />
              </button>
            </div>
          </div>

          {/* Mobile Dropdown Menu */}
          {showMobileMenu && (
            <div className="absolute top-full left-0 right-0 bg-white border-b border-white/40 shadow-lg z-50">
              <div className="p-4 border-b border-white/30">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center">
                    <User className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="font-medium text-[#1a1a2e]">{user?.full_name}</p>
                    <p className="text-sm text-neutral-500">{user?.email}</p>
                  </div>
                </div>
              </div>
              <button
                onClick={() => { handleLogout(); setShowMobileMenu(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50"
              >
                <LogOut className="w-5 h-5" />
                <span>Logout</span>
              </button>
            </div>
          )}
        </header>

        {/* Mobile Tabs */}
        <div className="sticky top-[57px] bg-white/40 px-3 py-2 z-30">
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('roles')}
              className={`flex-1 px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                activeTab === 'roles'
                  ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow'
                  : 'glass-card text-neutral-700'
              }`}
              data-testid="mobile-tab-roles"
            >
              <Briefcase className="w-4 h-4 inline mr-1" />
              Positions ({roles.length})
            </button>
            <button
              onClick={() => setActiveTab('applications')}
              className={`flex-1 px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                activeTab === 'applications'
                  ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow'
                  : 'glass-card text-neutral-700'
              }`}
              data-testid="mobile-tab-applications"
            >
              <FileText className="w-4 h-4 inline mr-1" />
              Apps ({applications.length})
            </button>
          </div>
        </div>

        {/* Mobile Main Content */}
        <main className="px-3 py-3">
          {/* Open Positions - Mobile */}
          {activeTab === 'roles' && (
            <div className="space-y-3" data-testid="mobile-roles-list">
              {roles.length === 0 ? (
                <div className="glass-card p-8 text-center">
                  <Briefcase className="w-10 h-10 text-neutral-300 mx-auto mb-3" />
                  <h3 className="font-medium text-[#1a1a2e]">No Open Positions</h3>
                  <p className="text-sm text-neutral-500 mt-1">Check back later</p>
                </div>
              ) : (
                roles.map(role => {
                  const hasApplied = applications.some(app => app.role_id === role._id);
                  return (
                    <div key={role._id} className="glass-card p-4" data-testid={`mobile-role-${role._id}`}>
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="text-base font-bold text-[#1a1a2e]">{role.designation}</h3>
                        <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full text-xs font-medium">
                          {role.department_name}
                        </span>
                      </div>
                      
                      {role.qualifications && (
                        <div className="mt-3">
                          <h4 className="text-xs font-semibold text-neutral-600 mb-1">Qualifications</h4>
                          <p className="text-xs text-neutral-600 line-clamp-2">{role.qualifications}</p>
                        </div>
                      )}
                      
                      {role.skills && (
                        <div className="mt-2">
                          <h4 className="text-xs font-semibold text-neutral-600 mb-1">Skills</h4>
                          <p className="text-xs text-neutral-600 line-clamp-2">{role.skills}</p>
                        </div>
                      )}
                      
                      <div className="mt-4">
                        {hasApplied ? (
                          <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-100 text-green-700 rounded-xl text-sm font-medium">
                            <CheckCircle className="w-4 h-4" />
                            Applied
                          </span>
                        ) : (
                          <button
                            onClick={() => openApplyModal(role)}
                            className="w-full px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl text-sm font-semibold"
                            data-testid={`mobile-apply-${role._id}`}
                          >
                            Apply Now
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* My Applications - Mobile */}
          {activeTab === 'applications' && (
            <div className="space-y-3" data-testid="mobile-applications-list">
              {applications.length === 0 ? (
                <div className="glass-card p-8 text-center">
                  <FileText className="w-10 h-10 text-neutral-300 mx-auto mb-3" />
                  <h3 className="font-medium text-[#1a1a2e]">No Applications</h3>
                  <p className="text-sm text-neutral-500 mt-1">Browse positions and apply</p>
                </div>
              ) : (
                applications.map(app => (
                  <div
                    key={app._id}
                    onClick={() => openAppDetail(app)}
                    className="glass-card p-4 active:bg-white/40"
                    data-testid={`mobile-app-${app._id}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-bold text-[#1a1a2e] truncate">{app.designation}</h3>
                        <p className="text-sm text-neutral-600">{app.department_name}</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-neutral-400 flex-shrink-0" />
                    </div>
                    
                    <div className="flex items-center justify-between mt-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-xl text-xs font-medium ${getStatusColor(app.status)}`}>
                        {getStatusIcon(app.status)}
                        {app.status}
                      </span>
                      <span className="text-xs text-neutral-500">
                        {new Date(app.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </main>
      </div>
      {applyModalJsx}
      {appDetailJsx}
      </>
    );
  }

  // ============================================
  // DESKTOP VIEW (Original)
  // ============================================
  return (
    <div className="min-h-screen bg-white/40" data-testid="career-dashboard-desktop">
      {/* Header */}
      <header className="bg-white border-b border-white/40 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-[#1a1a2e]">Careers Portal</span>
            </div>
            
            <div className="flex items-center gap-4">
              <ViewModeToggleSimple />
              <div className="flex items-center gap-2 text-neutral-700">
                <User className="w-5 h-5" />
                <span className="font-medium">{user?.full_name}</span>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 px-4 py-2 text-neutral-600 hover:text-[#1a1a2e] hover:bg-neutral-100 rounded-xl"
                data-testid="desktop-logout-btn"
              >
                <LogOut className="w-5 h-5" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex gap-4 mb-8">
          <button
            onClick={() => setActiveTab('roles')}
            className={`px-6 py-3 rounded-xl font-medium transition-all ${
              activeTab === 'roles'
                ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg'
                : 'glass-card text-neutral-700 hover:bg-white/60'
            }`}
            data-testid="desktop-tab-roles"
          >
            <Briefcase className="w-5 h-5 inline mr-2" />
            Open Positions ({roles.length})
          </button>
          <button
            onClick={() => setActiveTab('applications')}
            className={`px-6 py-3 rounded-xl font-medium transition-all ${
              activeTab === 'applications'
                ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg'
                : 'glass-card text-neutral-700 hover:bg-white/60'
            }`}
            data-testid="desktop-tab-applications"
          >
            <FileText className="w-5 h-5 inline mr-2" />
            My Applications ({applications.length})
          </button>
        </div>

        {/* Open Positions */}
        {activeTab === 'roles' && (
          <div className="grid gap-6">
            {roles.length === 0 ? (
              <div className="glass-card p-12 text-center">
                <Briefcase className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-[#1a1a2e]">No Open Positions</h3>
                <p className="text-neutral-500 mt-2">Check back later for new opportunities</p>
              </div>
            ) : (
              roles.map(role => {
                const hasApplied = applications.some(app => app.role_id === role._id);
                return (
                  <div key={role._id} className="glass-card p-6 hover:shadow-lg transition-all">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-bold text-[#1a1a2e]">{role.designation}</h3>
                          <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium">
                            {role.department_name}
                          </span>
                        </div>
                        
                        {role.qualifications && (
                          <div className="mt-4">
                            <h4 className="text-sm font-semibold text-neutral-700 mb-1">Qualifications</h4>
                            <p className="text-neutral-600 text-sm whitespace-pre-wrap">{role.qualifications}</p>
                          </div>
                        )}
                        
                        {role.skills && (
                          <div className="mt-4">
                            <h4 className="text-sm font-semibold text-neutral-700 mb-1">Skills Required</h4>
                            <p className="text-neutral-600 text-sm whitespace-pre-wrap">{role.skills}</p>
                          </div>
                        )}
                        
                        {role.scope_of_work && (
                          <div className="mt-4">
                            <h4 className="text-sm font-semibold text-neutral-700 mb-1">Scope of Work</h4>
                            <p className="text-neutral-600 text-sm whitespace-pre-wrap">{role.scope_of_work}</p>
                          </div>
                        )}
                      </div>
                      
                      <div className="ml-6">
                        {hasApplied ? (
                          <span className="px-4 py-2 bg-green-100 text-green-700 rounded-xl font-medium flex items-center gap-2">
                            <CheckCircle className="w-5 h-5" />
                            Applied
                          </span>
                        ) : (
                          <button
                            onClick={() => openApplyModal(role)}
                            className="px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-xl font-semibold hover:opacity-90 transition-all flex items-center gap-2"
                          >
                            Apply Now
                            <ChevronRight className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* My Applications */}
        {activeTab === 'applications' && (
          <div className="grid gap-6">
            {applications.length === 0 ? (
              <div className="glass-card p-12 text-center">
                <FileText className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-[#1a1a2e]">No Applications Yet</h3>
                <p className="text-neutral-500 mt-2">Browse open positions and apply</p>
              </div>
            ) : (
              applications.map(app => (
                <div
                  key={app._id}
                  onClick={() => openAppDetail(app)}
                  className="glass-card p-6 hover:shadow-lg transition-all cursor-pointer"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-bold text-[#1a1a2e]">{app.designation}</h3>
                      <p className="text-neutral-600 mt-1">{app.department_name}</p>
                      <div className="flex items-center gap-4 mt-3 text-sm text-neutral-500">
                        <span>Applied: {new Date(app.created_at).toLocaleDateString()}</span>
                        {app.expected_ctc && <span>Expected CTC: ₹{app.expected_ctc}</span>}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <span className={`px-4 py-2 rounded-xl font-medium flex items-center gap-2 ${getStatusColor(app.status)}`}>
                        {getStatusIcon(app.status)}
                        {app.status}
                      </span>
                      <ChevronRight className="w-5 h-5 text-neutral-400" />
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </main>

      {applyModalJsx}
      {appDetailJsx}
    </div>
  );
}
